import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { ParticipantListData } from "../../../../../models/participantData";
import { AppState } from "../../../../../redux";
import { showDialog } from "../../../../../redux/dialogs/actions";
import { editParticipantStatus } from "../../../../../redux/participants/actions";
import CustomSelect from "../../../../forms/selects/CustomSelect";
import "./Participants.scss";
import {
  ELearningParticipationStatus,
  ParticipationStatus,
} from "../../../../../models/enums/participationStatus.enum";
import { addEventToCart } from "../../../../../redux/cart/actions";
import { isEventOver } from "../../shared/checkEventIsOver";
import { TrainingType } from "../../../../../models/enums/trainingType.enum";
import usePermission from "../../../../../services/usePermissions";

interface ParticipantsStatusProps {
  participant: ParticipantListData;
  disabledSelect?: boolean;
  index: number;
  trainingType: TrainingType;
  isPartOfBlendedLearning?: boolean;
}

export const ParticipantsStatus: React.FC<ParticipantsStatusProps> = (props) => {
  const { participant, trainingType, index, isPartOfBlendedLearning, disabledSelect } =
    props;
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: AppState) => state.user.currentUser);
  const editEvent = useSelector((state: AppState) => state.event.editEvent);
  const { getParticipationStatusForSelectBasedOnUserType } = usePermission(
    currentUser?.user_type
  );
  editEvent.id = id;

  const options: {
    key: string;
    value: string | number;
    label: string;
    disabled?: boolean;
  }[] =
    trainingType === TrainingType.ELearning
      ? [
          {
            key: "Bestanden",
            value: ELearningParticipationStatus.Passed,
            label: "Bestanden",
          },
          {
            key: "Nicht bestanden",
            value: ELearningParticipationStatus.NotPassed,
            label: "Nicht bestanden",
          },
        ]
      : getParticipationStatusForSelectBasedOnUserType()
          .filter(
            (o) =>
              o.value !== ParticipationStatus.CancelledByStaff
          )
          .map((o) => ({
            ...o,
            label: o.label ?? o.key ?? String(o.value),
            disabled: o.value === ParticipationStatus.Booked,
          }));

  let statusLabel = "Status";
  if (trainingType === TrainingType.DefaultEvent && isPartOfBlendedLearning)
    statusLabel += " Veranstaltung";
  if (trainingType === TrainingType.ELearning) statusLabel += " E-Learning";

  return (
    <CustomSelect
      disabled={disabledSelect || trainingType === TrainingType.ELearning}
      name={"eventParticipation." + index}
      id={"participant-status" + index}
      label={statusLabel}
      labelId="participant-status-label"
      value={
        trainingType === TrainingType.ELearning
          ? participant.elearning_status
          : participant.status
      }
      noEmptyDefaultValue
      onChange={(event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let appendedText = "";
        if (
          event.target.value === ParticipationStatus.Booked &&
          !editEvent.max_seats_taken &&
          participant.status !== ParticipationStatus.Booked &&
          !isEventOver(editEvent.end)
        ) {
          appendedText =
            " Sie werden im nächsten Schritt zum Checkout weitergeleitet, welchen Sie stellvertretend für den Nutzer durchführen müssen.";
        } else if (
          event.target.value === ParticipationStatus.Booked &&
          !editEvent.max_seats_taken &&
          participant.status !== ParticipationStatus.Booked
        ) {
          appendedText =
            " Die Veranstaltung ist bereits beendet. Sie werden im nächsten Schritt zum Checkout weitergeleitet, welchen Sie stellvertretend für den Nutzer durchführen müssen.";
        } else if (
          event.target.value === ParticipationStatus.Booked &&
          editEvent.max_seats_taken &&
          !isEventOver(editEvent.end)
        ) {
          appendedText =
            " Die Veranstaltung ist ausgebucht. Sie werden im nächsten Schritt zum Checkout weitergeleitet.";
        } else if (
          event.target.value === ParticipationStatus.Booked &&
          editEvent.max_seats_taken
        ) {
          appendedText =
            " Die Veranstaltung ist bereits beendet und ausgebucht. Sie werden im nächsten Schritt zum Checkout weitergeleitet.";
        }

        dispatch(
          showDialog({
            title: "Status des Teilnehmers ändern",
            message:
              "Sind Sie sich sicher, dass Sie den Status von " +
              participant.user.fullname +
              ' auf "' +
              options.filter((o) => o.value === event.target.value)[0]?.label +
              '" ändern möchten?' +
              appendedText,
            action: () => {
              if (
                event.target.value === ParticipationStatus.Booked &&
                participant.status !== ParticipationStatus.Booked
              ) {
                // when moving participant to booked manually
                // TODO: +id and id of type number should stay for cartData
                dispatch(addEventToCart(id, participant.user.id));
                // history.push(routes.checkout);
              } else {
                dispatch(
                  editParticipantStatus(
                    editEvent,
                    participant.id,
                    event.target.value as number
                  )
                );
              }
            },
          })
        );
      }}
      options={options}
    />
  );
};

// TODO no translation yet
